import axios from 'axios';
import { Button, Divider, Grid, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';
import { ChangeEvent, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { AssignContactsModal } from '@modules/admin/AdminTools/Main/components/AssignContacts/AssignContactsModal';
import { formatDate, formatPhoneNumber, getOptions, queryParams, withQueryParams } from '@shared/commonFunctions';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { Roles } from '@shared/constants/constants';
import { dateFormatWithTime } from '@shared/constants/formats';
import { useTabs } from '@shared/hooks/useTabs';
import { useToggle } from '@shared/hooks/useToggle';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { useUsers } from '@shared/hooks/useUsers';
import { CancelButton } from '@shared/inputs/Buttons';
import { ISelectOption } from '@shared/inputs/Select';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { TextInput } from '@shared/inputs/TextInput';
import { IOption } from '@shared/interfaces';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectViewAsUserId, leadQueueCount } from '@store/reducers/headerSlice';
import { ViewHeightWrapper } from '@styles/CommonStyles';
import { colorTheme } from '@styles/muiTheme';
import { FlexEnd, Row } from '@styles/StyledDiv';
import { withOpenTab } from '@shared/HOCs/withOpenTab';
import { TabIndexEnum } from '@modules/ContactManager/components/Header/interfaces';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { DealsButton } from '@styles/StyledButtons';
import { useServerSide } from '@shared/hooks/useServerSide';
import { requestActivatePIN, requestActivatePinUser, requestAssignLeads, requestNewLead } from '../requests';
import { ILcQueueProps, ILeads, ILeadsQueueQueryParams, ILeadsResponse, IPurlPinResponse, IRequestLead } from '../types';

const RenderNewLeadResp = ({ newLeadResp }: { newLeadResp: IRequestLead }) => {
  const availableCount = newLeadResp?.availableRankedLeadCount + newLeadResp?.availableOtherLeadCount;

  return (
    <>
      <Typography mr="4px">{newLeadResp?.dripFeederName ?? ''}</Typography>
      <Typography
        fontWeight="bold"
        mr="4px"
        color={availableCount > 0 ? colorTheme.palette.error.main : ''}
      >
        {`(${availableCount} Available)`}
      </Typography>
      <Typography mr="10px">{`(Oldest ${formatDate(newLeadResp?.oldestLeadDate, {
        format: dateFormatWithTime,
      })}): ${newLeadResp?.currentPullCount}/${newLeadResp?.maxPulls}`}</Typography>
      <Divider
        flexItem
        orientation="vertical"
        variant="middle"
        sx={{ height: '19px' }}
      />
      <Typography
        fontWeight="bold"
        ml="10px"
      >{`Total: ${newLeadResp?.currentPullCount}/${newLeadResp?.maxPulls}`}</Typography>
    </>
  );
};

const columns: GridColDef[] = [
  dataGridCol('primaryLeadSourceName', {
    headerName: 'Source',
    flex: 1.6,
  }),
  dataGridCol('leadNumericId'),
  dataGridCol('dealNumericId'),
  dataGridCol('leadDate', {
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
    type: 'dateTime',
  }),
  dataGridCol('firstName'),
  dataGridCol('lastName'),
  dataGridCol('contactUser'),
  dataGridCol('state'),
  dataGridCol('timeZone'),
  dataGridCol('phoneNumber', {
    headerName: 'Phone Number',
    valueFormatter: ({ value }) => formatPhoneNumber(value),
  }),
  dataGridCol('loanAmount', gridCurrencyCell),
  dataGridCol('assetTag'),
  dataGridCol('lastCommunicationDate', {
    headerName: 'Last Cmc Date',
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    type: 'dateTime',
  }),
  dataGridCol('status'),
  dataGridCol('lastStatusUpdateDate', {
    headerName: 'Last Stat Update',
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
    type: 'dateTime',
  }),
  dataGridCol('lastCommentDate', {
    headerName: 'Last Cmt Date',
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
    type: 'dateTime',
  }),
].map((column) => ({ flex: 1, ...column }));

const LeadsPage = ({ title }: ILcQueueProps) => {
  const purlInputRef = useRef<HTMLInputElement>(null);
  const params = queryParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { openTab } = useTabs();
  const theme = useTheme();
  const viewAs = useAppSelector(selectViewAsUserId);
  const [editing, toggleEditing] = useToggle();

  const pageSize = 50;
  const [purlPin, setPurlPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [assignTo, setAssignTo] = useState<ISelectOption>();
  const [assignLeadsModalIsOpen, setAssignLeadsModalIsOpen] = useState(false);
  const [popUpLoading, setPopUpLoading] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [purlPinResponse, setPurlPinResponse] = useState<IPurlPinResponse | null>();
  const [pageLoading, setPageLoading] = useState(false);
  const [items, setItems] = useState<ILeads[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const { isAdmin, userId, userHasRoles } = useUserAccount();
  const isAssignLeads = isAdmin || userHasRoles([Roles.LC_MANAGER, Roles.LC_LEAD]);

  const { requestPayload, onSortModelChange, onFilterModelChange, handleClosePanel, onPageChange } = useServerSide();
  const {
    data: leadsResponse,
    mutate: reloadLead,
    isValidating: validateLead,
    error: leadError,
  } = useSWR<ILeadsResponse>(
    [
      withQueryParams(`/${SERVICES.SEARCH}/search/filtered/leadqueuebyid`, {
        queueId: params.queueId,
        viewAsUserId: viewAs?.userId,
      }),
      { ...requestPayload, pageSize },
    ],
    ([url, payload]) => axios.post(url, payload),
    { revalidateIfStale: true }
  );

  useEffect(() => {
    reloadLead();
  }, [requestPayload]);

  useEffect(() => {
    if (leadsResponse) {
      setItems(leadsResponse?.data?.items);
      setTotalCount(leadsResponse?.data?.totalCount);
      dispatch(leadQueueCount(leadsResponse?.data?.totalCount));
    }
  }, [leadsResponse]);

  const {
    data: newLeadResp,
    isValidating,
    error,
  } = useSWR<IRequestLead>(userId && `/${SERVICES.LEAD}/dripfeederhistory/${userId}`);

  const { mutate: reloadMenu } = useSWR(
    withQueryParams(
      `/${SERVICES.SEARCH}/search/menuitems`,
      viewAs.userId && {
        viewAsUserId: viewAs.userId,
      },
      false
    ),
    {
      revalidateOnMount: false,
    }
  );

  const { users } = useUsers();

  const dripFeederLoading = useMemo(() => (!newLeadResp && !error) || isValidating, [newLeadResp, isValidating, error]);

  const pullReadyText = useMemo(() => {
    let text = '';

    if (newLeadResp) {
      if (!newLeadResp.dripFeederName) {
        text = 'No Pulls Available';
      } else if (newLeadResp.maxPullsReached) {
        text = 'Max for day reached';
      } else if (newLeadResp.timeToNextPullInMilliSeconds === 0 || timeLeft <= 0) {
        text = 'Pull Ready';
      }
    }

    return text;
  }, [newLeadResp, timeLeft]);

  const disableButton = useMemo(
    () =>
      newLeadResp && (newLeadResp.maxPullsReached || newLeadResp.timeToNextPullInMilliSeconds > 0 || !newLeadResp.dripFeederName),
    [newLeadResp]
  );

  useEffect(() => {
    if (newLeadResp && !newLeadResp.maxPullsReached) {
      setTimeLeft(newLeadResp.timeToNextPullInMilliSeconds / 1000);
    }
  }, [newLeadResp]);

  useEffect(() => {
    if (timeLeft === 0) {
      setTimeLeft(0);
    }

    if (!timeLeft) return;

    const intervalId = setInterval(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timeLeft]);

  const rowClick = useCallback(
    (row: ILeads) => {
      const pageNumber = requestPayload?.pageNumber ?? 1;
      const offSetValue = pageSize * (pageNumber - 1);

      const recordIndex = items?.findIndex((item) => item.leadId === row.leadId);
      const recordNumber = recordIndex >= 0 ? offSetValue + recordIndex + 1 : 0;
      const queryParams: ILeadsQueueQueryParams = {
        id: row.contactId,
        leadId: row.leadId,
        tab: TabIndexEnum.Leads,
        queueId: params.queueId,
        recordNumber,
        isDealsQueue: false,
      };
      
      if (requestPayload?.orderBy) {
        queryParams.orderBy = requestPayload.orderBy;
      }
      
      if (requestPayload?.orderByDirection != null) {
        queryParams.orderByDirection = requestPayload.orderByDirection;
      }
      const route = withQueryParams('/contact-manager', queryParams, false);
      const title = `${row.firstName} ${row.lastName}`;
      openTab({ title, route, shouldCloseDuplicate: true });
      navigate(route);
    },
    [params.queueId, items, requestPayload?.pageNumber, pageSize]
  );

  const onAddNewContact = useCallback(() => {
    navigate('/admin-tools/add-new-contact');
  }, []);

  const onSelectionModelChange = useCallback((model: GridSelectionModel) => {
    model && setSelectionModel(model);
  }, []);

  const selectedLeads = useMemo(
    () => (items ?? []).filter((item) => selectionModel.includes(item?.leadId)),
    [items, selectionModel]
  );

  const popUpMessage = useMemo(() => {
    const message = `${!purlPinResponse?.showContactLinkButton ? 'PIN is already active for contact' : 'PIN has been activated.'
      }`;

    return message;
  }, [purlPinResponse]);

  const handleConfirmAssignLeads = useCallback(async () => {
    setPopUpLoading(true);
    await requestAssignLeads(assignTo?.id ?? '', selectedLeads);
    setSelectionModel([]);
    toggleEditing();
    setAssignTo(undefined);
    setAssignLeadsModalIsOpen(false);

    setTimeout(() => {
      reloadMenu();
      reloadLead();
      setPopUpLoading(false);
    }, 2000);
  }, [assignTo, selectedLeads]);

  const handleSelectChange = useCallback((value: unknown, user: IOption<string>) => {
    setAssignTo({
      id: (value as string) ?? '',
      name: user?.label ?? '',
    });
  }, []);

  const handleCloseAssignLeads = useCallback(() => {
    setAssignLeadsModalIsOpen(false);
    setAssignTo(undefined);
    setSelectionModel([]);
  }, []);

  const handleCancelButton = () => {
    toggleEditing();
    setAssignTo(undefined);
    setSelectionModel([]);
  };

  const onActivatePIN = useCallback(async () => {
    setLoading(true);
    const response = await requestActivatePIN(purlPin);
    if (response) {
      setPurlPinResponse(response as IPurlPinResponse);
    }
    setLoading(false);
  }, [purlPin]);

  const handleRequestNewLead = useCallback(async () => {
    setButtonLoader(true);
    const resp = await requestNewLead(userId as string);
    setButtonLoader(false);
    reloadMenu();

    if (resp) {
      const title = `${resp.contactFirstName} ${resp.contactLastName}`;
      const route = withQueryParams('/contact-manager', { id: resp.contactId }, false);

      openTab({ title, route });
      navigate(route);
    }
  }, [userId]);

  const transformUpperCaseFn = (s: string): string => s.toUpperCase();

  const handlePurlInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    upperCaseFn: (s: string) => string
  ): void => {
    const { selectionStart, selectionEnd, value } = e.target;
    const transformedValue = upperCaseFn(value);
    setPurlPin(transformedValue);

    if (purlInputRef.current) {
      setTimeout(() => {
        purlInputRef?.current?.setSelectionRange(selectionStart, selectionEnd);
      }, 0);
    }
  };

  const handleConfirm = useCallback(async () => {
    setPurlPinResponse(null);
    setPageLoading(true);
    const resp = purlPinResponse && (await requestActivatePinUser(purlPinResponse));
    setPageLoading(false);

    if (resp) {
      const title = purlPinResponse?.assignContactUserName ?? '';
      const route = withQueryParams('/contact-manager', { id: resp.contactId }, false);

      openTab({ title, route });
      navigate(route);
    }
  }, [purlPinResponse]);

  return (
    <ViewHeightWrapper>
      <LoaderBackdrop open={pageLoading} />

      <Box mb={-4}>
        <Typography
          variant="h3"
          color={theme.palette.primary.dark}
          mt="16px"
        >
          {title}
        </Typography>

        <Grid
          container
          spacing={1}
          mb={1}
        >
          <Grid
            item
            xs={editing ? 4 : 0}
          >
            {editing && (
              <SelectAutocomplete
                fullWidth
                value={assignTo?.id}
                placeholder="Select User"
                options={getOptions(users?.groupUsers, 'userId', 'userName')}
                onChange={handleSelectChange}
              />
            )}
          </Grid>
          <Grid
            item
            xs={editing ? 8 : 12}
          >
            <Row>
              {isAssignLeads && (
                <>
                  {editing ? (
                    <>
                      <Button
                        variant="contained"
                        disabled={!(assignTo && selectionModel.length)}
                        onClick={() => setAssignLeadsModalIsOpen(true)}
                      >
                        Assign Selected
                      </Button>
                      <CancelButton onClick={handleCancelButton} />
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={toggleEditing}
                    >
                      Assign Leads
                    </Button>
                  )}
                </>
              )}
              <FlexEnd style={{ width: '100%' }}>
                {dripFeederLoading || !newLeadResp ? (
                  ''
                ) : !newLeadResp?.dripFeederName ? (
                  'User is not part of a drip feeder set'
                ) : (
                  <RenderNewLeadResp newLeadResp={newLeadResp as IRequestLead} />
                )}
              </FlexEnd>
            </Row>
          </Grid>
        </Grid>
        <Row
          gap="30px"
          justify="space-between"
        >
          <Box sx={{ display: 'flex', gap: '30px' }}>
            <Button
              variant="contained"
              onClick={onAddNewContact}
            >
              Add New Contact
            </Button>
            <Divider
              flexItem
              orientation="vertical"
              variant="middle"
            />
            <Box sx={{ display: 'flex', gap: '5px' }}>
              <TextInput
                ref={purlInputRef}
                name="pin"
                placeholder="Enter PURL PIN"
                value={purlPin}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                onChange={(e) => handlePurlInputChange(e, transformUpperCaseFn)}
              />
              <Button
                variant="contained"
                disabled={!purlPin || loading}
                onClick={onActivatePIN}
              >
                {!loading ? 'Activate PIN/TIZ' : 'Loading...'}
              </Button>
            </Box>
          </Box>
          <Box
            display="flex"
            gap={1}
          >
            <Typography>{dripFeederLoading ? '' : timeLeft > 0 ? timeLeft.toFixed(0) : pullReadyText}</Typography>
            <DealsButton
              disabled={dripFeederLoading || newLeadResp?.maxPullsReached || (disableButton && timeLeft > 0)}
              size="medium"
              loading={buttonLoader}
              onClick={handleRequestNewLead}
            >
              Request New Lead
            </DealsButton>
          </Box>
        </Row>
      </Box>
      <PhoenixGrid
        strict
        noButton
        withHeader
        showClearAllFilter
        disableSelectionOnClick
        clearAllFilterBtnSx={{ bottom: isAssignLeads ? '100px' : '68px', left: `${Number(title.length) + 1}em` }}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        name=""
        gridId={title}
        checkboxSelection={editing}
        columns={columns}
        loading={(!items && !leadError) || validateLead}
        rows={items && !validateLead ? items : []}
        selectionModel={selectionModel}
        pageSize={pageSize}
        rowCount={totalCount ?? 0}
        getRowId={(row) => row.leadId}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        onPreferencePanelClose={handleClosePanel}
        onSelectionModelChange={onSelectionModelChange}
        onPageChange={onPageChange}
        onRowClick={({ row }: { row: ILeads }) => rowClick(row)}
      />

      {assignLeadsModalIsOpen && (
        <AssignContactsModal
          isLeadQueue
          loading={popUpLoading}
          handleClose={handleCloseAssignLeads}
          handleConfirm={handleConfirmAssignLeads}
          selectedLeads={selectedLeads}
          selectedUser={assignTo}
        />
      )}

      {!!purlPinResponse?.contactId && (
        <AlertModal
          open
          width="400px"
          title="Alert"
          subtitle={popUpMessage}
          cancelButtonText="Close"
          buttonText="Go To Contact"
          hideSave={!purlPinResponse?.showContactLinkButton}
          onClose={() => setPurlPinResponse(null)}
          onConfirm={handleConfirm}
        />
      )}
    </ViewHeightWrapper>
  );
};

export const Leads = withOpenTab(LeadsPage);
